import { useEffect, useRef } from "react";
import { sendSspVisibility } from "./sspVisibility";

const VISIBILITY_THRESHOLD = 0.5;
const SSP_VISIBILITY_DURATION = 1000;
const LEGACY_VISIBILITY_DURATION = 2000;
const viewedTeasers = new Set();
const viewedTeasersByBlock = new Map();
const sentTeasersByBlock = new Map();
const pendingTeasersByBlock = new Map();
const blockSizes = new Map();

let wasCollectSent = false;

export const getVisibilityOccurrenceKey = (teaserId, blockId, showId, sspTeaser) => {
    if (sspTeaser && sspTeaser._ssp_source === true) {
        return `ssp:${sspTeaser.bid_uuid || sspTeaser.request_id || `${showId}:${blockId}:${teaserId}`}`;
    }
    return `legacy:${teaserId}`;
};

export const getVisibilityDuration = (sspTeaser) =>
    sspTeaser && sspTeaser._ssp_source === true ? SSP_VISIBILITY_DURATION : LEGACY_VISIBILITY_DURATION;

const addProto = (url) => {
    return url.startsWith("http") ? url : `https://${url}`;
};

const normalizeIds = (values, parser) => {
    if (Array.isArray(values)) {
        return values
            .map(parser)
            .filter(
                (value) =>
                    value !== undefined && value !== null && value !== "" && value !== "undefined" && value !== "null"
            )
            .filter((value) => typeof value !== "number" || !Number.isNaN(value));
    }
    if (values === undefined || values === null) {
        return [];
    }
    const parsed = parser(values);
    if (parsed === undefined || parsed === null || parsed === "" || parsed === "undefined" || parsed === "null") {
        return [];
    }
    if (typeof parsed === "number" && Number.isNaN(parsed)) {
        return [];
    }
    return [parsed];
};

const normalizeNumberIds = (values) => normalizeIds(values, Number);
const normalizeStringIds = (values) => normalizeIds(values, String);

const sendBlockData = (blockType, teasersToSend, blockId, showId, campaignIds, creativeIds, bidderIds, bidderCids) => {
    const sentTeasers = sentTeasersByBlock.get(blockType) || new Set();
    teasersToSend.forEach((teaser) => sentTeasers.add(teaser.occurrenceKey));
    sentTeasersByBlock.set(blockType, sentTeasers);

    const isFirst = !wasCollectSent;
    const visUrl = addProto(isFirst ? "data.24smi.net/collect" : "data.24smi.net/collect_teaser");
    wasCollectSent = true;

    const teaserIds = teasersToSend.map((teaser) => Number(teaser.id));
    const paramsObj = {
        obj: blockId.toString(),
        teaser_ids: teaserIds.join(","),
        show_id: showId.toString(),
    };
    if (!isFirst) {
        const campaignIdsNum = campaignIds.map(Number);
        const creativeIdsStr = creativeIds.map(String);
        paramsObj.campaign_ids = campaignIdsNum.join(",");
        paramsObj.creative_ids = creativeIdsStr.join(",");
    }
    const params = new URLSearchParams(paramsObj);

    fetch(`${visUrl}?${params.toString()}`, {
        method: "GET",
        mode: "no-cors",
        keepalive: true,
    }).catch((error) => {
        console.error("Ошибка при отправке данных о видимости тизеров:", error);
    });
};

const checkAndSendBlockData = (
    blockType,
    teaser,
    blockId,
    showId,
    campaignIds,
    creativeIds,
    bidderIds,
    bidderCids,
    occurrenceKey
) => {
    let alreadyPending = false;
    pendingTeasersByBlock.forEach((teasersSet, block) => {
        if (block !== blockType) {
            for (let t of teasersSet) {
                if (t.occurrenceKey === occurrenceKey) {
                    alreadyPending = true;
                }
            }
        }
    });
    if (alreadyPending) return;

    const pendingTeasers = pendingTeasersByBlock.get(blockType) || new Set();
    const blockSize = blockSizes.get(blockType);
    const teaserWithContext = {
        ...teaser,
        blockId,
        showId,
        campaignIds: normalizeNumberIds(campaignIds),
        creativeIds: normalizeStringIds(creativeIds),
        bidderIds: normalizeNumberIds(bidderIds),
        bidderCids: normalizeStringIds(bidderCids),
        occurrenceKey,
    };
    if (blockType.includes("Feed")) {
        pendingTeasers.add(teaserWithContext);
        pendingTeasersByBlock.set(blockType, pendingTeasers);
        if (pendingTeasers.size >= 5) {
            const allCampaignIds = Array.from(pendingTeasers)
                .map((t) => t.campaignIds)
                .flat();
            const allCreativeIds = Array.from(pendingTeasers)
                .map((t) => (Array.isArray(t.creativeIds) ? t.creativeIds : [t.creativeIds]))
                .flat()
                .map(String);
            const allBidderIds = Array.from(pendingTeasers)
                .map((t) => t.bidderIds)
                .flat()
                .map(Number);
            const allBidderCids = Array.from(pendingTeasers)
                .map((t) => t.bidderCids)
                .flat()
                .map(String);
            sendBlockData(
                blockType,
                Array.from(pendingTeasers),
                blockId,
                showId,
                allCampaignIds,
                allCreativeIds,
                allBidderIds,
                allBidderCids
            );
            pendingTeasers.clear();
            pendingTeasersByBlock.set(blockType, pendingTeasers);
        }
        return;
    }
    pendingTeasers.add(teaserWithContext);
    pendingTeasersByBlock.set(blockType, pendingTeasers);
    if (pendingTeasers.size >= blockSize) {
        const allCampaignIds = Array.from(pendingTeasers)
            .map((t) => t.campaignIds)
            .flat();
        const allCreativeIds = Array.from(pendingTeasers)
            .map((t) => (Array.isArray(t.creativeIds) ? t.creativeIds : [t.creativeIds]))
            .flat()
            .map(String);
        const allBidderIds = Array.from(pendingTeasers)
            .map((t) => t.bidderIds)
            .flat()
            .map(Number);
        const allBidderCids = Array.from(pendingTeasers)
            .map((t) => t.bidderCids)
            .flat()
            .map(String);
        sendBlockData(
            blockType,
            Array.from(pendingTeasers),
            blockId,
            showId,
            allCampaignIds,
            allCreativeIds,
            allBidderIds,
            allBidderCids
        );
        pendingTeasers.clear();
        pendingTeasersByBlock.set(blockType, pendingTeasers);
    }
};

if (typeof window !== "undefined") {
    const sendPendingTeasers = () => {
        pendingTeasersByBlock.forEach((teasers, blockType) => {
            if (teasers.size > 0) {
                const groupedTeasers = Array.from(teasers).reduce((acc, teaser) => {
                    const key = `${teaser.blockId}_${teaser.showId}`;
                    if (!acc[key]) {
                        acc[key] = {
                            blockId: teaser.blockId,
                            showId: teaser.showId,
                            teasers: [],
                            campaignIds: [],
                            creativeIds: [],
                            bidderIds: [],
                            bidderCids: [],
                        };
                    }
                    acc[key].teasers.push(teaser);
                    if (teaser.campaignIds) {
                        if (Array.isArray(teaser.campaignIds)) {
                            acc[key].campaignIds.push(...teaser.campaignIds.map(Number));
                        } else {
                            acc[key].campaignIds.push(Number(teaser.campaignIds));
                        }
                    }
                    if (teaser.creativeIds) {
                        acc[key].creativeIds.push(...teaser.creativeIds);
                    }
                    if (teaser.bidderIds) {
                        acc[key].bidderIds.push(...teaser.bidderIds);
                    }
                    if (teaser.bidderCids) {
                        acc[key].bidderCids.push(...teaser.bidderCids);
                    }
                    return acc;
                }, {});

                Object.values(groupedTeasers).forEach((group) => {
                    group.creativeIds = group.creativeIds.flat().map(String);
                    group.campaignIds = group.campaignIds.flat().map(Number);
                    group.bidderIds = group.bidderIds.flat().map(Number);
                    group.bidderCids = group.bidderCids.flat().map(String);
                    sendBlockData(
                        blockType,
                        group.teasers,
                        group.blockId,
                        group.showId,
                        group.campaignIds,
                        group.creativeIds,
                        group.bidderIds,
                        group.bidderCids
                    );
                });
                teasers.clear();
                pendingTeasersByBlock.set(blockType, teasers);
            }
        });
    };

    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            sendPendingTeasers();
        }
    });

    window.addEventListener("pagehide", sendPendingTeasers);

    window.addEventListener("beforeunload", sendPendingTeasers);
}

export const useVisibilityTracker = (
    teaserId,
    blockId,
    blockType,
    blockSize,
    showId,
    campaignIds,
    creativeIds,
    bidderIds,
    bidderCids,
    eventtrackers,
    sspTeaser
) => {
    const elementRef = useRef(null);
    const timerRef = useRef(null);
    const isVisibleRef = useRef(false);
    const impressionTrackedKeyRef = useRef(null);
    const occurrenceKey = getVisibilityOccurrenceKey(teaserId, blockId, showId, sspTeaser);
    const visibilityDuration = getVisibilityDuration(sspTeaser);

    if (blockSize && !blockSizes.has(blockType)) {
        blockSizes.set(blockType, blockSize);
    }

    useEffect(() => {
        if (impressionTrackedKeyRef.current === occurrenceKey) {
            return;
        }
        impressionTrackedKeyRef.current = occurrenceKey;
        if (Array.isArray(eventtrackers)) {
            eventtrackers.forEach(function (tracker) {
                if (tracker && tracker.event === 1 && tracker.method === 1 && tracker.url) {
                    fetch(tracker.url, { mode: "no-cors" });
                }
            });
        }
    }, [occurrenceKey, eventtrackers]);

    useEffect(() => {
        if (!elementRef.current || viewedTeasers.has(occurrenceKey)) {
            return;
        }
        isVisibleRef.current = false;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                const isVisible = entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;

                if (isVisible && !isVisibleRef.current) {
                    isVisibleRef.current = true;
                    timerRef.current = setTimeout(() => {
                        if (isVisibleRef.current && !viewedTeasers.has(occurrenceKey)) {
                            viewedTeasers.add(occurrenceKey);

                            if (Array.isArray(eventtrackers)) {
                                eventtrackers.forEach(function (tracker) {
                                    if (tracker && tracker.event === 2 && tracker.method === 1 && tracker.url) {
                                        fetch(tracker.url, { mode: "no-cors" });
                                    }
                                });
                            }

                            sendSspVisibility({
                                teasers: [sspTeaser],
                                blockId,
                                showId,
                            });

                            if (!viewedTeasersByBlock.has(blockType)) {
                                viewedTeasersByBlock.set(blockType, new Set());
                            }
                            viewedTeasersByBlock.get(blockType).add(occurrenceKey);

                            checkAndSendBlockData(
                                blockType,
                                { id: teaserId },
                                blockId,
                                showId,
                                campaignIds,
                                creativeIds,
                                bidderIds,
                                bidderCids,
                                occurrenceKey
                            );
                        }
                    }, visibilityDuration);
                } else if (!isVisible && isVisibleRef.current) {
                    isVisibleRef.current = false;
                    if (timerRef.current) {
                        clearTimeout(timerRef.current);
                    }
                }
            },
            {
                threshold: VISIBILITY_THRESHOLD,
            }
        );

        observer.observe(elementRef.current);

        return () => {
            observer.disconnect();
            isVisibleRef.current = false;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            const pendingTeasers = pendingTeasersByBlock.get(blockType);
            if (pendingTeasers && pendingTeasers.size > 0) {
                const allCampaignIds = Array.from(pendingTeasers)
                    .map((t) => (Array.isArray(t.campaignIds) ? t.campaignIds : [t.campaignIds]))
                    .flat()
                    .map(Number);
                const allCreativeIds = Array.from(pendingTeasers)
                    .map((t) => (Array.isArray(t.creativeIds) ? t.creativeIds : [t.creativeIds]))
                    .flat()
                    .map(String);
                const allBidderIds = Array.from(pendingTeasers)
                    .map((t) => (Array.isArray(t.bidderIds) ? t.bidderIds : [t.bidderIds]))
                    .flat()
                    .map(Number);
                const allBidderCids = Array.from(pendingTeasers)
                    .map((t) => (Array.isArray(t.bidderCids) ? t.bidderCids : [t.bidderCids]))
                    .flat()
                    .map(String);
                sendBlockData(
                    blockType,
                    Array.from(pendingTeasers),
                    blockId,
                    showId,
                    allCampaignIds,
                    allCreativeIds,
                    allBidderIds,
                    allBidderCids
                );
                pendingTeasers.clear();
                pendingTeasersByBlock.set(blockType, pendingTeasers);
            }
        };
    }, [
        occurrenceKey,
        teaserId,
        blockId,
        blockType,
        showId,
        campaignIds,
        creativeIds,
        bidderIds,
        bidderCids,
        eventtrackers,
        sspTeaser,
        visibilityDuration,
    ]);

    return elementRef;
};
